import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Chip,
  MultiSelect,
  PrimaryButton,
  SearchField,
  SecondaryButton,
  StatusBar,
  Typography,
  VenueIcon,
} from '@weareopenr/component-library';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { publishingChannelIntegrationTypes } from 'src/constants';
import SelectableListWithControls from 'src/components/SelectableListWithControls/SelectableListWithControls';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { useVenueCategories } from 'src/hooks/useVenueCategories/useVenueCategories';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { getChannelLabel, getPricingChannels } from 'src/helpers/channels/getPricingChannels';
import { getSelectedSystemIds } from 'src/helpers/venueChannelAvailability/venueChannelAvailability';
import {
  buildRuleDestinations,
  countExistingRulesForVenue,
  getVenueDestinationState,
  IExistingRuleDestination,
} from 'src/helpers/attributeRuleDestinations/attributeRuleDestinations';
import { filterVenueOptions, venueCategoriesToSelectOptions } from 'src/utils/venueCategories/mappedVenueCategories';
import { ICreateAttributeRuleDestination } from 'src/api/createAttributeRule';
import { HAS_RULES_CHIP, NO_CHANNELS_SELECTED, NO_VENUES_SELECTED, OVERRIDE_RULES_CHIP } from '../constants';
import styles from './Steps.module.scss';

export interface IRuleDestinationsValue {
  channelIds: string[];
  venueIds: string[];
}

interface RuleDestinationsStepProps {
  value: IRuleDestinationsValue;
  onChange: (value: IRuleDestinationsValue) => void;
  existingDestinations: IExistingRuleDestination[];
  onBack: () => void;
  onSubmit: (destinations: ICreateAttributeRuleDestination[]) => void;
  isSubmitting?: boolean;
  errorMessage?: string;
}

const renderExistingRulesChip = (ruleCount: number, isSelected: boolean) => {
  if (ruleCount === 0) return undefined;
  if (isSelected) return <Chip label={OVERRIDE_RULES_CHIP} backgroundColour="honey" />;
  return <Chip label={HAS_RULES_CHIP} backgroundColour="grape" />;
};

export const RuleDestinationsStep = ({
  value: { channelIds, venueIds },
  onChange,
  existingDestinations,
  onBack,
  onSubmit,
  isSubmitting,
  errorMessage,
}: RuleDestinationsStepProps) => {
  const { data: externalSystems, isLoading: isChannelsLoading } = useExternalSystems();
  const { data: venues, isLoading: isVenuesLoading } = useVenues();
  const { data: venueCategoriesData, nestedCategories, isLoading: isCategoriesLoading } = useVenueCategories();
  const [venueSearchText, setVenueSearchText] = useState('');
  const [filteredCategoryIds, setFilteredCategoryIds] = useState<string[]>([]);
  const debouncedVenueSearch = useDebounce(venueSearchText);

  const channels = useMemo(
    () =>
      getPricingChannels(externalSystems).filter((channel) =>
        publishingChannelIntegrationTypes.includes(channel.integrationType),
      ),
    [externalSystems],
  );

  const channelOptions = useMemo(
    () => channels.map((channel) => ({ value: channel.extensionInstanceId, label: getChannelLabel(channel) })),
    [channels],
  );

  const selectedSystemIds = useMemo(() => getSelectedSystemIds(channelIds, channels), [channelIds, channels]);

  const categoryOptions = useMemo(() => venueCategoriesToSelectOptions(nestedCategories), [nestedCategories]);

  const allVenueOptions = useMemo(
    () =>
      (venues ?? []).map((venue) => ({
        id: venue.venue_id,
        label: venue.name,
        ...getVenueDestinationState(venue, selectedSystemIds),
        chip: renderExistingRulesChip(
          countExistingRulesForVenue(venue.venue_id, channelIds, existingDestinations),
          venueIds.includes(venue.venue_id),
        ),
      })),
    [venues, selectedSystemIds, channelIds, venueIds, existingDestinations],
  );

  const venueOptions = useMemo(
    () =>
      filterVenueOptions(allVenueOptions, debouncedVenueSearch, filteredCategoryIds, venueCategoriesData?.categories),
    [allVenueOptions, debouncedVenueSearch, filteredCategoryIds, venueCategoriesData],
  );

  const selectedVenueOptions = useMemo(
    () => allVenueOptions.filter((option) => venueIds.includes(option.id)),
    [allVenueOptions, venueIds],
  );

  const handleChannelsChange = (ids: string[]) => {
    const remainingSystemIds = getSelectedSystemIds(ids, channels);
    const stillSelectable = venueIds.filter((id) => {
      const venue = venues?.find((candidate) => candidate.venue_id === id);
      return !!venue && !getVenueDestinationState(venue, remainingSystemIds).disabled;
    });
    onChange({ channelIds: ids, venueIds: stillSelectable });
  };

  const hasChannels = channelIds.length > 0;
  const hasVenues = venueIds.length > 0;

  const destinations = useMemo(
    () => buildRuleDestinations(channelIds, venueIds, venues ?? [], channels),
    [channelIds, venueIds, venues, channels],
  );

  const handleSubmit = () => onSubmit(destinations);

  return (
    <div className={styles['content']}>
      <Typography weight="bold" className={styles['step']}>
        Step 2 of 2
      </Typography>
      <Typography size="xxlarge" weight="semi-bold" className={styles['title']}>
        Define rule destinations
      </Typography>

      <Autocomplete
        multiple
        name="channels"
        label="Channels"
        options={channelOptions}
        value={channelIds}
        onChange={handleChannelsChange}
        loading={isChannelsLoading}
        disabled={isChannelsLoading}
        limitChips={2}
      />

      <div className={styles['divider']} />

      <SelectableListWithControls
        className={styles['venue-list']}
        options={venueOptions}
        selectedOptionsIds={venueIds}
        onOptionChange={(ids) => onChange({ channelIds, venueIds: ids })}
        collapsible={{ selectedOptionsData: selectedVenueOptions, isLoading: isVenuesLoading }}
        listDetails={{ title: 'Venues:', isLoading: isVenuesLoading }}
      >
        <SearchField
          isDisabled={isVenuesLoading}
          placeholder="Search for a venue"
          onChange={(event) => setVenueSearchText(event.target.value)}
        />
        <MultiSelect
          Icon={VenueIcon}
          name="venue-categories"
          label="Categories"
          className={styles['venue-categories-filter']}
          selected={filteredCategoryIds}
          options={categoryOptions}
          onChange={setFilteredCategoryIds}
          disabled={isCategoriesLoading}
          disableRecursiveParentSelection
        />
      </SelectableListWithControls>

      {!hasChannels && <StatusBar className={styles['banner']} variant="info" message={NO_CHANNELS_SELECTED} />}
      {hasChannels && !hasVenues && (
        <StatusBar className={styles['banner']} variant="warning" message={NO_VENUES_SELECTED} />
      )}

      <div className={styles['actions']}>
        <ButtonContainer
          margin="none"
          left={
            <SecondaryButton
              buttonStyle="outline"
              type="button"
              size="full-width"
              disabled={isSubmitting}
              onClick={onBack}
            >
              Back
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              type="button"
              colour="blueberry"
              size="full-width"
              loading={isSubmitting}
              disabled={isSubmitting || (hasChannels && destinations.length === 0)}
              onClick={handleSubmit}
            >
              Create
            </PrimaryButton>
          }
        />
      </div>

      {errorMessage && <StatusBar className={styles['error-banner']} variant="error" message={errorMessage} />}
    </div>
  );
};

export default RuleDestinationsStep;
